<ng-template [cdkPortalOutlet]="selectedPortal"></ng-template>

  <!-- Call Header -->
  <app-call-header
    [class.d-none]="isLoading || callStore.getIsFullScreen()"
    [isLoading]="!!playerService?.showLoadingScreen"
    [title]="callStore.getCall()?.callName"
    [sessionId]="callStore.getCall()?.practiceCallUuid"
    (sessionIdClicked)="copyToClipboard()"
  >
    @if (callMode === 'normal') {
      <app-call-controls
        [isMicOn]="callStore.getIsMicOn()"
        (microphoneClicked)="handleMuteClick()"
        [isVideoOn]="controlService?.isVideoOn || false"
        [isCameraAvailable]="!deviceSettingsService?.isCameraUnavailable"
        (videoClicked)="handleCameraClick()"
        (endCallClicked)="handleEndCallClick()"
        [disableMicButton]="disableMicButton"
        [disableEndCallButton]="disableEndCallButton"
        [hideCameraToggle]="callStore.getHideCameraToggle()"
      />
    } @else {
      <app-visual-aid-call-controls
        [isMicOn]="callStore.getIsMicOn()"
        (microphoneClicked)="handleMuteClick()"
        [isPaused]="callStore.getIsPaused()"
        (playbackClicked)="handlePauseClick()"
        (endCallClicked)="handleEndCallClick()"
        [disableMicButton]="disableMicButton"
        [disableEndCallButton]="disableEndCallButton"
        [showPauseButton]="showPauseButton"
      />
    }
  </app-call-header>

  @if (callStore.getIsReconnectingAudio()) {
    <div class="reconnecting-banner">{{ 'call.RECONNECTING_AUDIO' | transloco }}</div>
  }

  <!-- Call Main Content -->
  <ng-container [ngSwitch]="callMode">
    <app-call-main *ngSwitchCase="'normal'" [class.d-none]="isLoading">
      @if (isScreenSharing) {
        <div class="screenshare-status" avatarSlot>
          @if (canReopenPip) {
            <button
              qai-button
              class="screenshare-status__reopen"
              kind="primary"
              size="xsmall"
              (click)="handlePipReopenClick()"
            >
              {{ 'call.SCREENSHARE_PIP_REOPEN_LABEL' | transloco }}
            </button>
          }
          <qai-badge
            class="screenshare-status__badge"
            kind="mist-blue"
            shape="rectangle"
            size="small"
            [label]="'call.SCREENSHARE_ACTIVE_LABEL' | transloco"
          />
        </div>
      }
      <qai-call-video
        class="avatar_video"
        avatarSlot
        [displayName]="avatarDisplayName"
        [isSpeaking]="showAvatarSpeakingBorder"
        [description]="aiNotice() ?? undefined" nameBadgeKind="gray-dark"
        [indicatorStatus]="showAvatarLoadingIndicator ? 'loading' : 'audio'"
      >
        <div class="videosStack" videoSlot>
          <video
            class="backgroundAvatarVideo"
            id="video-background-loop"
            preload="auto"
            [src]="playerService?.loopClipFilename"
            muted
            [attr.playsinline]="true"
            [attr.webkit-playsinline]="true"
          ></video>
          <video
            *ngFor="let item of playerService?.practiceCallClips || []"
            [id]="'avatar-video-' + item.index"
            [src]="item.src"
            [className]="item.className"
            preload="auto"
            [attr.playsinline]="true"
            [attr.webkit-playsinline]="true"
          ></video>
          <ng-container *ngIf="controlService?.isRealTimeCall">
            <div class="video-canvas-wrapper" [class.avatar-in-pip]="isAvatarInPip">
              <video
                id="avatar-video-realtime"
                #avatarVideoRealtime
                [appAvatarPlayer]="realtimeService"
                [attr.playsinline]="true"
                [attr.webkit-playsinline]="true"
                [ngClass]="realtimeService?.customBackgroundUrl ? 'avatar-video-hidden' : 'avatar-video-visible'"
                class="avatar-video-realtime"
                ></video>
              <canvas *ngIf="realtimeService?.customBackgroundUrl"
               id="chroma-canvas"
               #chromaCanvas
               class="chroma-canvas"></canvas>
          </div>
            <ng-container *ngTemplateOutlet="screenPreview" />
            <audio
              id="avatar-audio-realtime"
              [attr.playsinline]="true"
              [attr.webkit-playsinline]="true"
            ></audio>
          </ng-container>
        </div>
        <qai-call-audio-indicator
          *ngIf="showAvatarSpeakingIndicator"
          size="medium"
          status="audio"
        />
        <qai-call-audio-indicator
          *ngIf="showAvatarLoadingIndicator"
          size="medium"
          status="loading"
        />
        <qai-call-captions
          *ngIf="controlService?.isCaptionsOn && playerService?.canPlayVideo"
          [maxLines]="4"
          >{{ playerService?.avatarSpeech() }}</qai-call-captions
        >
      </qai-call-video>
      <app-call-hint hintSlot maxHeight="none" *ngIf="showHints" [hint]="callStore.getHints()" />
      <app-call-hint-tour
        hintSlot
        *ngIf="showHintsTour"
        (continueTourClicked)="startCallAfterTour()"
      />
      <qai-call-video
        userSlot
        class="userVideo"
        [displayName]="userName.first"
        [fullName]="userName.full"
        [showInitials]="showUserInitials"
        [isMuted]="!callStore.getIsMicOn() || callStore.getIsPaused()"
        [isSpeaking]="showUserSpeakingBorder"
      >
        <video
          videoSlot
          id="user-video"
          muted
          [attr.playsinline]="true"
          [attr.webkit-playsinline]="true"
        ></video>
        <qai-call-audio-indicator
          *ngIf="showUserSpeakingIndicator"
          size="small"
          status="audio"
        />
      </qai-call-video>
    </app-call-main>

    <app-call-main-visual-aid
      *ngSwitchCase="'visualAid'"
      [controlService]="controlService"
      [playerService]="playerService"
      (dispplayVisualAidClicked)="onDisplayVisualAidClick($event)"
      (currentPageChanged)="onPageChanged($event)"
      [enableVisualAid]="enableVisualAid"
    >
      <qai-call-video
        avatarSlot
        [displayName]="avatarDisplayName"
        [isSpeaking]="showAvatarSpeakingBorder"
        [description]="aiNotice() ?? undefined" nameBadgeKind="gray-dark"
        [indicatorStatus]="showAvatarLoadingIndicator ? 'loading' : 'audio'"
      >
        <div class="videosStack" videoSlot>
          <video
            class="backgroundAvatarVideo"
            id="video-background-loop"
            preload="auto"
            [src]="playerService?.loopClipFilename"
            muted
            [attr.playsinline]="true"
            [attr.webkit-playsinline]="true"
          ></video>
          <video
            *ngFor="let item of playerService?.practiceCallClips || []; trackBy: trackBySrc"
            [id]="'avatar-video-' + item.index"
            [src]="item.src"
            [className]="item.className"
            preload="auto"
            [attr.playsinline]="true"
            [attr.webkit-playsinline]="true"
            (canplay)="avatarVideoIsReady(item)"
            (error)="avatarLoadingError($event, item)"
          ></video>
        </div>
        <qai-call-audio-indicator
          *ngIf="showAvatarSpeakingIndicator"
          size="medium"
          status="audio"
        />
        <qai-call-audio-indicator
          *ngIf="showAvatarLoadingIndicator"
          size="medium"
          status="loading"
        />
        <qai-call-captions
          *ngIf="controlService?.isCaptionsOn && playerService?.canPlayVideo"
          [maxLines]="3"
          >{{ playerService?.avatarSpeech() }}</qai-call-captions
        >
      </qai-call-video>
      <app-call-hint hintSlot *ngIf="showHints" [hint]="callStore.getHints()" />
      <app-call-hint-tour
        hintSlot
        *ngIf="showHintsTour"
        (continueTourClicked)="startCallAfterTour()"
      />
      <qai-call-video
        userSlot
        class="userVideo"
        [displayName]="userName.first"
        [fullName]="userName.full"
        [showInitials]="showUserInitials"
        [isMuted]="!callStore.getIsMicOn() || callStore.getIsPaused()"
        [isSpeaking]="showUserSpeakingBorder"
      >
        <video
          videoSlot
          id="user-video"
          muted
          [attr.playsinline]="true"
          [attr.webkit-playsinline]="true"
        ></video>
        <qai-call-audio-indicator
          *ngIf="showUserSpeakingIndicator"
          size="small"
          status="audio"
        />
      </qai-call-video>
      <app-call-visual-aid-v2
        visualAidSlot
        [controlService]="controlService"
        [playerService]="playerService"
      ></app-call-visual-aid-v2>
    </app-call-main-visual-aid>

    <app-visual-aid-call-main
      *ngSwitchCase="'realTime'"
      [controlService]="controlService"
      [playerService]="playerService"
      [class.d-none]="isLoading"
    >
      <qai-call-video
        avatarSlot
        [displayName]="avatarDisplayName"
        [isSpeaking]="showAvatarSpeakingBorder"
        [description]="aiNotice() ?? undefined" nameBadgeKind="gray-dark"
        [indicatorStatus]="showAvatarLoadingIndicator ? 'loading' : 'audio'"
      >
        <div class="videosStack" videoSlot>
          <ng-container *ngIf="controlService?.isRealTimeCall">
            <div class="video-canvas-wrapper" [class.avatar-in-pip]="isAvatarInPip">
              <video
                id="avatar-video-realtime"
                #avatarVideoRealtime
                [appAvatarPlayer]="realtimeService"
                [attr.playsinline]="true"
                [attr.webkit-playsinline]="true"
                [ngClass]="realtimeService?.customBackgroundUrl ? 'avatar-video-hidden' : 'avatar-video-visible'"
                class="avatar-video-realtime"
              ></video>
              <canvas *ngIf="realtimeService?.customBackgroundUrl"
                id="chroma-canvas"
                #chromaCanvas
                class="chroma-canvas"></canvas>
            </div>
            <ng-container *ngTemplateOutlet="screenPreview" />
            <audio
              id="avatar-audio-realtime"
              [attr.playsinline]="true"
              [attr.webkit-playsinline]="true"
            ></audio>
          </ng-container>
        </div>
        <qai-call-audio-indicator
          *ngIf="showAvatarSpeakingIndicator"
          size="medium"
          status="audio"
        />
        <qai-call-audio-indicator
          *ngIf="showAvatarLoadingIndicator"
          size="medium"
          status="loading"
        />
        <qai-call-captions
          *ngIf="controlService?.isCaptionsOn && playerService?.canPlayVideo"
          [maxLines]="3"
          >{{ playerService?.avatarSpeech() }}</qai-call-captions
        >
      </qai-call-video>
      <app-call-hint
        hintSlot
        *ngIf="showHints"
        [hint]="callStore.getHints()"
        [maxHeight]="callStore.getIsFullScreen() ? '20px' : '120px'"
      />
      <app-call-hint-tour
        hintSlot
        *ngIf="showHintsTour"
        (continueTourClicked)="startCallAfterTour()"
      />
    </app-visual-aid-call-main>
  </ng-container>

  <!-- Call footer -->
  <ng-container [ngSwitch]="callMode">
    <app-call-footer
      *ngSwitchCase="'normal'"
      [class.d-none]="isLoading"
      [isCaptionsOn]="controlService?.isCaptionsOn || false"
      (settingsClicked)="deviceSettingsService?.displayDeviceSettingsModal()"
      (captionsClicked)="controlService?.toggleCaptions()"
      [time]="controlService?.timer || ''"
      [showPauseButton]="showPauseButton"
      [isPauseEnabled]="isPauseEnabled"
      [showContinueButton]="showContinueButton"
      [isPaused]="callStore.getIsPaused()"
      (playbackClicked)="handlePauseClick()"
      [isRealTimeCall]="controlService?.isRealTimeCall || false"
    >
      <div
        *ngIf="showContinueButton"
        qaiSlot="continue"
        [class.showcase]="
          controlService?.displayTour && !controlService?.displayTourHint
        "
      >
        <button
          qai-button
          size="large"
          [kind]="canContinueNext ? 'primary' : 'tertiary'"
          [disabled]="isContinueButtonDisabled"
          (click)="handleContinueClick()"
        >
          {{ "call.CONTINUE" | transloco }}
        </button>
      </div>
      <app-call-controls
        [isMicOn]="callStore.getIsMicOn()"
        [disableMicButton]="disableMicButton"
        (microphoneClicked)="handleMuteClick()"
        [isVideoOn]="controlService?.isVideoOn || false"
        [isCameraAvailable]="!deviceSettingsService?.isCameraUnavailable"
        [disableEndCallButton]="disableEndCallButton"
        (videoClicked)="
          deviceSettingsService?.isCameraUnavailable
            ? deviceSettingsService?.displayDeviceSettingsModal()
            : handleCameraClick()
        "
        (endCallClicked)="handleEndCallClick()"
        [hideCameraToggle]="callStore.getHideCameraToggle()"
      />
      @if (showScreenshareButton) {
        <app-screenshare-button
          [isScreenSharing]="isScreenSharing"
          [disabled]="isScreenshareDisabled"
          (clicked)="handleScreenshareClick()"
        />
      }
    </app-call-footer>
    <app-visual-aid-call-footer
      *ngSwitchDefault
      [isCaptionsOn]="controlService?.isCaptionsOn || false"
      (settingsClicked)="deviceSettingsService?.displayDeviceSettingsModal()"
      (captionsClicked)="controlService?.toggleCaptions()"
      [class.d-none]="isLoading || callStore.getIsFullScreen()"
    >
      <app-visual-aid-call-controls
        [isMicOn]="callStore.getIsMicOn()"
        (microphoneClicked)="handleMuteClick()"
        [isPaused]="callStore.getIsPaused()"
        [isPauseEnabled]="isPauseEnabled"
        (playbackClicked)="handlePauseClick()"
        (endCallClicked)="handleEndCallClick()"
        [disableMicButton]="disableMicButton"
        [disableEndCallButton]="disableEndCallButton"
        [showPauseButton]="showPauseButton"
      />
    </app-visual-aid-call-footer>
  </ng-container>

<!-- User inactivity alert -->
<app-call-top-alert
  *ngIf="callStore?.getDisplayAlert()"
  [title]="realtimeService?.inactivityAlertTitle || ''"
  [message]="realtimeService?.inactivityAlertMessage || ''"
></app-call-top-alert>

<!-- Settings modal -->
<app-device-settings-modal
  *ngIf="deviceSettingsService?.displayModal"
  [deviceSettingsService]="deviceSettingsService"
  [controlService]="controlService"
></app-device-settings-modal>

<!-- End call early -->
<app-end-call-early
  *ngIf="controlService?.isEndCallTriggered && !callStore.getDisplayPostCall()"
  [controlService]="controlService"
  [playerService]="playerService"
  [realtimeService]="realtimeService"
></app-end-call-early>

<!-- End call succesful -->
<app-post-call
  *ngIf="callStore.getDisplayPostCall() && !hideResultsForLearners"
  [controlService]="controlService"
  [playerService]="playerService"
></app-post-call>

<app-post-call-hidden-results
  *ngIf="callStore.getDisplayPostCall() && hideResultsForLearners"
  [controlService]="controlService"
></app-post-call-hidden-results>

<!-- No response detected -->
<app-no-response-detected
  *ngIf="callStore.getOpenNoResponseModal()"
  [controlService]="controlService"
  [playerService]="playerService"
  [deviceSettingsService]="deviceSettingsService"
  [realtimeService]="realtimeService"
></app-no-response-detected>

<!-- Pause modal -->
<app-call-pause
  *ngIf="
    controlService &&
    callStore.getIsPaused() &&
    !deviceSettingsService?.displayModal &&
    !controlService.isEndCallTriggered &&
    !callStore.getOpenNoResponseModal() &&
    !controlService.displayTour &&
    !controlService.displayTourHint
  "
  [control]="controlService"
  [player]="playerService"
  [deviceSettings]="deviceSettingsService"
></app-call-pause>

<!-- Tour -->
<app-call-tour
  *ngIf="showTour"
  [control]="controlService"
  [player]="playerService"
  [realtime]="realtimeService"
></app-call-tour>

<ng-template #screenPreview>
  <video
    *ngIf="screenPreviewStream as stream"
    class="screen-share-preview"
    [srcObject]="stream"
    [muted]="true"
    autoplay
    [attr.playsinline]="true"
    [attr.webkit-playsinline]="true"
  ></video>
</ng-template>
